/* Sensor, weapon and threat envelopes as GeoJSON.
 *
 * These are the highest-value layers in the product. They answer the two questions
 * that drive every operational-level decision:
 *
 *   "can I be seen?"   → sensor envelopes, hostile and own
 *   "can I shoot?"     → weapon / DLZ envelopes
 *
 * ── v0.9 HONESTY NOTE ────────────────────────────────────────────────────────
 * These are GEOMETRIC envelopes: a geodesic circle or arc at the instrumented
 * range from the dataset. They are NOT model output. A real sensor envelope is
 * terrain-masked, RCS-dependent, weather-attenuated and duct-affected, and it is
 * not a circle. The engine produces that in v1.0 (SPEC-01 §6). Until then the UI
 * labels them "geometric" so a demo cannot imply more than is true.
 */
import type { Feature, FeatureCollection, Polygon } from 'geojson'
import { circleRing, sectorRing } from './geodesy'
import type { OrbisContact, OrbisEntity } from '../data/types'

export type EnvelopeKind = 'sensor' | 'weapon' | 'threat'

interface EnvelopeProps {
  kind: EnvelopeKind
  affiliation: string
  entityId: string
  label: string
  rangeNm: number
  sensorKind?: string
  degraded?: boolean
}

function poly(ring: [number, number][], props: EnvelopeProps): Feature<Polygon, EnvelopeProps> {
  return { type: 'Feature', properties: props, geometry: { type: 'Polygon', coordinates: [ring] } }
}

/** Own-force sensor coverage. Skips silent and destroyed sensors — EMCON is visible. */
export function sensorEnvelopes(entities: OrbisEntity[]): FeatureCollection<Polygon, EnvelopeProps> {
  const features: Feature<Polygon, EnvelopeProps>[] = []

  for (const e of entities) {
    // A radar under EMCON silent is not radiating, so it has no coverage to draw.
    // Showing a ring for a silent emitter would be actively misleading.
    if (e.emcon.radar === 'silent') continue

    for (const c of e.components) {
      if (c.kind !== 'sensor' || !c.rangeNm || c.health === 'destroyed') continue
      // A degraded sensor is modelled here as reduced range rather than hidden.
      const range = c.health === 'degraded' ? c.rangeNm * 0.6 : c.rangeNm
      const half = c.arcHalfWidthDeg ?? 180
      const ring =
        half >= 180
          ? circleRing({ lon: e.lon, lat: e.lat }, range)
          : sectorRing(
              { lon: e.lon, lat: e.lat },
              range,
              e.courseDeg + (c.arcOffsetDeg ?? 0),
              half,
            )
      features.push(
        poly(ring, {
          kind: 'sensor',
          affiliation: e.affiliation,
          entityId: e.id,
          label: `${e.designation} · ${c.name}`,
          rangeNm: range,
          sensorKind: c.sensorKind,
          degraded: c.health === 'degraded',
        }),
      )
    }
  }
  return { type: 'FeatureCollection', features }
}

/** Weapon / DLZ envelopes — largest effective range per platform, not per round. */
export function weaponEnvelopes(entities: OrbisEntity[]): FeatureCollection<Polygon, EnvelopeProps> {
  const features: Feature<Polygon, EnvelopeProps>[] = []

  for (const e of entities) {
    const armed = e.components.filter(
      (c) => c.weaponRangeNm && c.health !== 'destroyed',
    )
    if (armed.length === 0) continue

    // One ring at the longest-reaching weapon. Drawing a ring per round buries the
    // operator in concentric circles for no decision value.
    const best = armed.reduce((a, b) => ((b.weaponRangeNm ?? 0) > (a.weaponRangeNm ?? 0) ? b : a))
    features.push(
      poly(circleRing({ lon: e.lon, lat: e.lat }, best.weaponRangeNm!), {
        kind: 'weapon',
        affiliation: e.affiliation,
        entityId: e.id,
        label: `${e.designation} · ${best.name}`,
        rangeNm: best.weaponRangeNm!,
      }),
    )
  }
  return { type: 'FeatureCollection', features }
}

/** Assessed hostile threat envelopes (SAM sites etc.) — the "can I be shot?" layer. */
export function threatEnvelopes(contacts: OrbisContact[]): FeatureCollection<Polygon, EnvelopeProps> {
  const features: Feature<Polygon, EnvelopeProps>[] = []
  for (const c of contacts) {
    if (!c.threatRangeNm) continue
    features.push(
      poly(circleRing({ lon: c.lon, lat: c.lat }, c.threatRangeNm), {
        kind: 'threat',
        affiliation: c.affiliation,
        entityId: c.id,
        label: `${c.track} · ${c.classification}`,
        rangeNm: c.threatRangeNm,
      }),
    )
  }
  return { type: 'FeatureCollection', features }
}

export const EMPTY: FeatureCollection<Polygon, EnvelopeProps> = {
  type: 'FeatureCollection',
  features: [],
}
